import React, { useState } from 'react';
import { Input, Button, Modal, Spin, Checkbox, InputNumber } from 'antd';
import {
  StepBackwardOutlined, CaretLeftOutlined,
  CaretRightOutlined, StepForwardOutlined,
  PlusOutlined, EditOutlined, DeleteOutlined,
  SaveOutlined, CloseOutlined, SearchOutlined,
} from '@ant-design/icons';
import { useProductMasterForm } from './hooks/useProductMasterForm';
import './PRDForm.css';

const PRDForm: React.FC = () => {
  const {
    mode, loading,
    prdCode, prdName, fullPrdCode, unit, prdSort,
    pcsCode, processing, speci, component, org,
    uWidthC, fWidthC, wightM, colorCode, color,
    isActive, memo, cUser, cDate,
    setPrdCode, setPrdName, setFullPrdCode, setUnit, setPrdSort,
    setPcsCode, setProcessing, setSpeci, setComponent, setOrg,
    setUWidthC, setFWidthC, setWightM, setColorCode, setColor,
    setIsActive, setMemo,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save, doDelete,
    quickSearch, recordPosition, recordCount,
  } = useProductMasterForm();

  const [searchKey, setSearchKey] = useState('');
  const isEditing = mode === 'add' || mode === 'edit';

  return (
    <Spin spinning={loading}>
      <div className="prd-form">
        <h2>產品主檔建檔</h2>

        {/* Quick Search */}
        <div className="prd-search-bar">
          <Input
            placeholder="輸入代號或名稱搜尋"
            value={searchKey}
            onChange={(e) => setSearchKey(e.target.value)}
            onPressEnter={() => quickSearch(searchKey)}
            style={{ width: 240 }}
            disabled={isEditing}
          />
          <Button icon={<SearchOutlined />} onClick={() => quickSearch(searchKey)} disabled={isEditing}>
            搜尋
          </Button>
          <span className="prd-record-info">{recordPosition} / {recordCount}</span>
        </div>

        {/* Navigation */}
        <div className="prd-nav-bar">
          <Button icon={<StepBackwardOutlined />} onClick={navFirst} disabled={isEditing}>首筆</Button>
          <Button icon={<CaretLeftOutlined />} onClick={navPrev} disabled={isEditing}>前筆</Button>
          <Button icon={<CaretRightOutlined />} onClick={navNext} disabled={isEditing}>次筆</Button>
          <Button icon={<StepForwardOutlined />} onClick={navLast} disabled={isEditing}>末筆</Button>
        </div>

        {/* Form Fields */}
        <div className="prd-fields">
          <div className="prd-field-group">
            <div className="prd-field-row">
              <label>材料代號</label>
              <Input value={fullPrdCode} onChange={(e) => setFullPrdCode(e.target.value)}
                maxLength={40} disabled={mode === 'edit'} style={{ width: 200 }} />
            </div>
            <div className="prd-field-row">
              <label>商業代號</label>
              <Input value={prdCode} onChange={(e) => setPrdCode(e.target.value)}
                maxLength={40} disabled={mode === 'edit'} style={{ width: 200 }} />
            </div>
          </div>

          <div className="prd-field-row">
            <label>材料名稱</label>
            <Input value={prdName} onChange={(e) => setPrdName(e.target.value)}
              maxLength={200} disabled={!isEditing} style={{ width: 400 }} />
          </div>

          <div className="prd-field-group">
            <div className="prd-field-row">
              <label>庫存單位</label>
              <Input value={unit} onChange={(e) => setUnit(e.target.value)}
                maxLength={10} disabled={!isEditing} style={{ width: 120 }} />
            </div>
            <div className="prd-field-row">
              <label>產品類型</label>
              <Input value={prdSort} onChange={(e) => setPrdSort(e.target.value)}
                maxLength={10} disabled={!isEditing} style={{ width: 120 }} />
            </div>
            <div className="prd-field-row">
              <label>組織代碼</label>
              <Input value={org} onChange={(e) => setOrg(e.target.value)}
                maxLength={10} disabled={!isEditing} style={{ width: 120 }} />
            </div>
          </div>

          <div className="prd-field-group">
            <div className="prd-field-row">
              <label>加工代號</label>
              <Input value={pcsCode} onChange={(e) => setPcsCode(e.target.value)}
                maxLength={20} disabled={!isEditing} style={{ width: 140 }} />
            </div>
            <div className="prd-field-row">
              <label>加工名稱</label>
              <Input value={processing} onChange={(e) => setProcessing(e.target.value)}
                maxLength={200} disabled={!isEditing} style={{ width: 200 }} />
            </div>
          </div>

          <div className="prd-field-row">
            <label>材料規格</label>
            <Input.TextArea value={speci} onChange={(e) => setSpeci(e.target.value)}
              rows={2} disabled={!isEditing} style={{ width: 500 }} />
          </div>

          <div className="prd-field-row">
            <label>材料成份</label>
            <Input.TextArea value={component} onChange={(e) => setComponent(e.target.value)}
              rows={2} disabled={!isEditing} style={{ width: 500 }} />
          </div>

          <div className="prd-field-group">
            <div className="prd-field-row">
              <label>可用幅寬(cm)</label>
              <InputNumber value={uWidthC} onChange={(v) => setUWidthC(v || 0)}
                disabled={!isEditing} style={{ width: 120 }} />
            </div>
            <div className="prd-field-row">
              <label>全幅(cm)</label>
              <InputNumber value={fWidthC} onChange={(v) => setFWidthC(v || 0)}
                disabled={!isEditing} style={{ width: 120 }} />
            </div>
            <div className="prd-field-row">
              <label>重量(g/m2)</label>
              <InputNumber value={wightM} onChange={(v) => setWightM(v || 0)}
                disabled={!isEditing} style={{ width: 120 }} />
            </div>
          </div>

          <div className="prd-field-group">
            <div className="prd-field-row">
              <label>色號</label>
              <Input value={colorCode} onChange={(e) => setColorCode(e.target.value)}
                maxLength={20} disabled={!isEditing} style={{ width: 120 }} />
            </div>
            <div className="prd-field-row">
              <label>色名</label>
              <Input value={color} onChange={(e) => setColor(e.target.value)}
                maxLength={100} disabled={!isEditing} style={{ width: 200 }} />
            </div>
          </div>

          <div className="prd-field-row">
            <label>備註</label>
            <Input.TextArea value={memo} onChange={(e) => setMemo(e.target.value)}
              rows={2} disabled={!isEditing} style={{ width: 500 }} />
          </div>

          <div className="prd-field-row">
            <label>啟用</label>
            <Checkbox checked={isActive} onChange={(e) => setIsActive(e.target.checked)}
              disabled={!isEditing} />
          </div>

          {/* Audit fields (read-only) */}
          <div className="prd-field-group">
            <div className="prd-field-row">
              <label>建立人員</label>
              <Input value={cUser} disabled style={{ width: 120 }} />
            </div>
            <div className="prd-field-row">
              <label>建立日期</label>
              <Input value={cDate ? cDate.substring(0, 10) : ''} disabled style={{ width: 140 }} />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="prd-actions">
          {!isEditing ? (
            <>
              <Button type="primary" icon={<PlusOutlined />} onClick={startAdd}>新增</Button>
              <Button icon={<EditOutlined />} onClick={startEdit} disabled={recordCount === 0}>修改</Button>
              <Button danger icon={<DeleteOutlined />} disabled={recordCount === 0}
                onClick={() => {
                  Modal.confirm({
                    title: '確認刪除',
                    content: `確定要刪除 ${fullPrdCode} 嗎？`,
                    onOk: doDelete,
                  });
                }}>
                刪除
              </Button>
            </>
          ) : (
            <>
              <Button type="primary" icon={<SaveOutlined />} onClick={save}>存檔</Button>
              <Button icon={<CloseOutlined />} onClick={cancel}>還原</Button>
            </>
          )}
        </div>
      </div>
    </Spin>
  );
};

export default PRDForm;
